import React from 'react';
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  computeAssemblyComparison,
  findAirframeAssembly,
  parseValueByUnit,
  formatValueByUnit
} from '../tsrComparisonUtils';
import { generateAssemblyCode, formatDecimalToYearsMonths } from '../componentFormUtils';
import { getLiveRawForTsr, liveTsrContextFromRecord } from '../../../utils/liveTlbUsageMapping';

const AirframeComparisonSection = ({
  timeSinceRecords,
  assemblies,
  currentUsageTotals = null,
  effectiveTlbUsageMapping = null,
  entityLabel = 'Component',
  liveDue = false
}) => {
  const { assembly: airframe, error } = findAirframeAssembly(assemblies || []);
  const airframeRecords = airframe?.timeSinceRecords || [];
  const currentValues = currentUsageTotals?.values || null;
  const useLiveDue = liveDue;

  const typeName = airframe?.type?.name || airframe?.typeName || '';
  const showTypeName = typeName === 'Airframe' || typeName === 'Airframe Assembly';

  const formulaCaption = useLiveDue
    ? `Installed At Airframe Value (snapshot) = Airframe_at_install; Due At Airframe Value (Live) = Airframe_at_install + (Component_limit - Component_elapsed_at_install); Remaining until Due (Live) = Due - Airframe_live_elapsed`
    : `Installed At Airframe = max(0, Airframe_elapsed - ${entityLabel}_elapsed); Due At Airframe = Airframe_elapsed + ${entityLabel}_remaining`;

  const hasNonEmpty = (v) => v != null && String(v).trim() !== '';

  const formatSignedValueByUnit = (numericValue, unit) => {
    if (numericValue == null || Number.isNaN(numericValue)) return '';
    const trimmedUnit = String(unit || '').trim();
    const isNegative = Number(numericValue) < 0;
    const signPrefix = isNegative ? '-' : '';

    if (trimmedUnit === 'Hours') {
      const absMinutes = Math.abs(Math.floor(numericValue));
      const hours = Math.floor(absMinutes / 60);
      const minutes = absMinutes % 60;
      return `${signPrefix}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    if (trimmedUnit === 'Landings') {
      return `${signPrefix}${Math.floor(Math.abs(numericValue)).toString()}`;
    }

    if (trimmedUnit === 'Years') {
      return `${signPrefix}${formatDecimalToYearsMonths(Math.abs(numericValue))}`;
    }

    if (trimmedUnit === 'Calendar Days') {
      return `${signPrefix}${Math.floor(Math.abs(numericValue)).toString()}`;
    }

    if (trimmedUnit === 'Cycles') {
      const v = Math.round(Math.abs(numericValue) * 100) / 100;
      return `${signPrefix}${v}`;
    }

    return `${numericValue}`;
  };

  const computeLiveDueAndRemainingAtAirframe = (unit, record) => {
    if (!currentValues || typeof currentValues !== 'object') return null;

    const { raw: airframeLiveRaw } = getLiveRawForTsr(
      currentValues,
      effectiveTlbUsageMapping,
      liveTsrContextFromRecord(record, { assemblyRole: 'Airframe' })
    );

    if (airframeLiveRaw == null) return null;

    const limitRaw = record?.limit?.value;
    if (!hasNonEmpty(limitRaw)) return null;

    try {
      const airframeLiveNumeric = parseValueByUnit(airframeLiveRaw, unit);
      const limitNumeric = parseValueByUnit(limitRaw, unit);

      const airframeAtInstallRaw = record?.airframeValueAtInstallation?.value;
      const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;

      if (!hasNonEmpty(airframeAtInstallRaw) || !hasNonEmpty(componentElapsedAtInstallRaw)) return null;

      const airframeAtInstallNumeric = parseValueByUnit(airframeAtInstallRaw, unit);
      const componentElapsedAtInstallNumeric = parseValueByUnit(componentElapsedAtInstallRaw, unit);

      // When component elapsed tracks airframe usage 1:1, due airframe total is:
      // Due_airframe = Airframe_at_install + (Component_limit - Component_elapsed_at_install)
      const dueAirframeNumeric = airframeAtInstallNumeric + (limitNumeric - componentElapsedAtInstallNumeric);

      // Remaining until due from now:
      // Remaining = Due_airframe - Airframe_live
      const remainingUntilDueNumeric = dueAirframeNumeric - airframeLiveNumeric;

      // Elapsed since installed (live)
      // With the 1:1 “component usage tracks airframe usage during flying” assumption:
      // Component_elapsed_live - Component_elapsed_at_install == Airframe_live - Airframe_at_install
      const elapsedSinceInstalledNumeric = airframeLiveNumeric - airframeAtInstallNumeric;

      return {
        dueDisplay: formatValueByUnit(dueAirframeNumeric, unit),
        remainingDisplay: formatSignedValueByUnit(remainingUntilDueNumeric, unit),
        remainingUntilDueNumeric,
        elapsedSinceInstalledDisplay: formatSignedValueByUnit(elapsedSinceInstalledNumeric, unit),
        elapsedSinceInstalledNumeric
      };
    } catch {
      return null;
    }
  };

  const getLiveDueAtAirframeReason = (unit, record) => {
    if (!currentValues || typeof currentValues !== 'object') {
      return 'N/A: Live/current aircraft usage totals are not available.';
    }
    const { raw: airframeLiveRaw, key: liveKey, reason: mapReason } = getLiveRawForTsr(
      currentValues,
      effectiveTlbUsageMapping,
      liveTsrContextFromRecord(record, { assemblyRole: 'Airframe' })
    );
    if (airframeLiveRaw == null) {
      return mapReason
        ? `N/A: ${mapReason}`
        : `N/A: No live value for TLB field '${liveKey || '?'}' in currentUsageTotals.values.`;
    }

    const limitRaw = record?.limit?.value;
    if (!hasNonEmpty(limitRaw)) {
      return 'N/A: Due At Airframe Value is computed from TSR limit.value (component life limit/frequency). This TSR row has no limit.value; remainingTime does not drive the live due calculation.';
    }

    const airframeAtInstallRaw = record?.airframeValueAtInstallation?.value;
    const componentElapsedAtInstallRaw = record?.elapsedTime?.value ?? record?.installedValue?.value;
    if (!hasNonEmpty(airframeAtInstallRaw) || !hasNonEmpty(componentElapsedAtInstallRaw)) {
      return 'N/A: Live Due/Remaining requires airframeValueAtInstallation and this component elapsed at installation (elapsedTime.value).';
    }

    return 'N/A: Unable to compute due/remaining from live + installation snapshots.';
  };

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">{useLiveDue ? 'As compared to Current Live Airframe' : 'As compared to Airframe'}</Typography>
      </Box>
      {error ? (
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Reference Assembly: {generateAssemblyCode(airframe) || '—'} (Assembly Id: {airframe?._id || '—'})
              {showTypeName ? ` · Type: ${typeName}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {formulaCaption}
            </Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Label</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Installed At Airframe Value</TableCell>
                  <TableCell>Due At Airframe Value</TableCell>
                  {useLiveDue ? <TableCell>Remaining until Due (Live)</TableCell> : null}
                  {useLiveDue ? <TableCell>Elapsed since installed (Live)</TableCell> : null}
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeSinceRecords && timeSinceRecords.length > 0 ? (
                  timeSinceRecords.map((record, index) => {
                    const comparison = computeAssemblyComparison(record, airframeRecords);

                    const unit =
                      record.elapsedTime?.unit ||
                      record.limit?.unit ||
                      record.remainingTime?.unit ||
                      '';

                    const airframeSnapshotStr = record.airframeValueAtInstallation?.value;
                    const remainingStr = record.remainingTime?.value;

                    let installedDisplay = null;
                    let dueDisplay = null;
                    let liveRemainingUntilDueNumeric = null;
                    let liveRemainingDisplay = null;
                    let liveElapsedSinceInstalledNumeric = null;
                    let liveElapsedSinceInstalledDisplay = null;

                    if (
                      airframeSnapshotStr != null &&
                      String(airframeSnapshotStr).trim() !== '' &&
                      unit
                    ) {
                      try {
                        const installedNumeric = parseValueByUnit(airframeSnapshotStr, unit);
                        installedDisplay = formatValueByUnit(installedNumeric, unit);
                      } catch (e) {
                        installedDisplay = null;
                      }
                    }

                    if (installedDisplay == null) {
                      installedDisplay =
                        record.airframeValueAtInstallation?.value != null &&
                        String(record.airframeValueAtInstallation.value).trim() !== ''
                          ? record.airframeValueAtInstallation.value
                          : null;
                    }

                    if (dueDisplay == null) {
                      if (useLiveDue) {
                        // Live Due + Remaining (Hours/Landings only)
                        const computed = computeLiveDueAndRemainingAtAirframe(unit, record);
                        dueDisplay = computed?.dueDisplay ?? null;
                        liveRemainingUntilDueNumeric = computed?.remainingUntilDueNumeric ?? null;
                        liveRemainingDisplay = computed?.remainingDisplay ?? null;
                        liveElapsedSinceInstalledNumeric = computed?.elapsedSinceInstalledNumeric ?? null;
                        liveElapsedSinceInstalledDisplay = computed?.elapsedSinceInstalledDisplay ?? null;
                      } else {
                        // Snapshot-based Due At Airframe Value (used by Edit Assembly flow)
                        if (
                          airframeSnapshotStr != null &&
                          String(airframeSnapshotStr).trim() !== '' &&
                          remainingStr != null &&
                          String(remainingStr).trim() !== '' &&
                          unit
                        ) {
                          try {
                            const installedNumeric = parseValueByUnit(airframeSnapshotStr, unit);
                            const remainingNumeric = parseValueByUnit(remainingStr, unit);
                            const dueNumeric = installedNumeric + remainingNumeric;
                            dueDisplay = formatValueByUnit(dueNumeric, unit);
                          } catch (e) {
                            dueDisplay = null;
                          }
                        }

                        if (dueDisplay == null) {
                          dueDisplay =
                            comparison.dueAtAssembly != null && comparison.dueAtAssembly !== ''
                              ? comparison.dueAtAssembly
                              : null;
                        }
                      }
                    }

                    return (
                      <TableRow key={index}>
                        <TableCell>{record.type}</TableCell>
                        <TableCell>{record.label}</TableCell>
                        <TableCell>{record.elapsedTime?.unit || record.limit?.unit || record.remainingTime?.unit || ''}</TableCell>
                        <TableCell>
                          {installedDisplay != null && installedDisplay !== '' ? installedDisplay : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {dueDisplay != null && dueDisplay !== '' ? dueDisplay : 'N/A'}
                        </TableCell>
                        {useLiveDue ? (
                          <TableCell
                            sx={{
                              color: liveRemainingUntilDueNumeric != null && liveRemainingUntilDueNumeric < 0 ? 'error.main' : undefined,
                              fontWeight: liveRemainingUntilDueNumeric != null && liveRemainingUntilDueNumeric < 0 ? 700 : undefined
                            }}
                          >
                            {liveRemainingDisplay != null && liveRemainingDisplay !== ''
                              ? liveRemainingDisplay
                              : 'N/A'}
                          </TableCell>
                        ) : null}
                        {useLiveDue ? (
                          <TableCell
                            sx={{
                              color: liveElapsedSinceInstalledNumeric != null && liveElapsedSinceInstalledNumeric < 0 ? 'error.main' : undefined,
                              fontWeight: liveElapsedSinceInstalledNumeric != null && liveElapsedSinceInstalledNumeric < 0 ? 700 : undefined
                            }}
                          >
                            {liveElapsedSinceInstalledDisplay != null && liveElapsedSinceInstalledDisplay !== ''
                              ? liveElapsedSinceInstalledDisplay
                              : 'N/A'}
                          </TableCell>
                        ) : null}
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {useLiveDue
                            ? (dueDisplay == null || dueDisplay === '' ? getLiveDueAtAirframeReason(unit, record) : '')
                            : (comparison.message || '')}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                  <TableCell colSpan={useLiveDue ? 8 : 6} align="center">
                      Add Time Since Records to see airframe comparisons here.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Grid>
  );
};

export default AirframeComparisonSection;
